import {Controller} from '@hotwired/stimulus';
import {fetchPlaceIdForPostCodeResult, fetchPostcodeAddresses} from './helpers/api';

export default class extends Controller {
  static targets = [
    'placeId',
    'address',
    'latitude',
    'longitude',
    'queryLabel',
    'query',
    'flowSwitcher',
    'udprn',
    'addressDetails',
    'addressResults',
    'postCode',
    /* Field containers */
    'addressDetailsContainer',
    'udprnContainer',
    'queryContainer',
    'queryError',
    'mapContainer'
  ];

  static values = {
    address: Object,
    defaultInputType: String,
    inputType: String,
    bounds: Object,
    idealPostcodesApiKey: String,
    postcodeResults: Array,
    udprn: String,
    addressDetails: String,
    queryError: String,
    queryErrorCount: Number,
    postCodeSupported: Boolean,
    edit: Boolean,
    loading: Boolean
  }

  /**
   * Initial page load. Checks if there's an existing address to edit and sets up the form accordingly.
   * @return {Promise<void>}
   */
  async connect() {
    // If postCodeSupported is false, force google input type and hide flow switcher
    if (this.hasPostCodeSupportedValue && this.postCodeSupportedValue === false) {
      this.inputTypeValue = 'google';
      this.flowSwitcherTarget.classList.add('d-none');
    }
    // Initialize the form
    // First check if there's all the data needed for postcode edit form
    if (this.addressValue.post_code &&
      this.addressValue.address &&
      this.addressValue.latitude &&
      this.addressValue.longitude &&
      this.addressValue.place_id &&
      this.addressValue.udprn) {

      /* Set all the labels */
      this.queryLabelTarget.textContent = 'Postcode';
      this.queryTarget.value = this.addressValue.post_code;
      this.queryTarget.placeholder = 'Enter postcode';

      this.addressTarget.value = this.addressValue.address;
      this.latitudeTarget.value = this.addressValue.latitude;
      this.longitudeTarget.value = this.addressValue.longitude;
      this.placeIdTarget.value = this.addressValue.place_id;
      this.addressDetailsTarget.value = this.addressValue.address_details;
      this.postCodeTarget.value = this.addressValue.post_code;

      /* Show the relevant fields */
      this.queryContainerTarget.classList.remove('d-none');
      this.udprnContainerTarget.classList.remove('d-none');
      this.addressDetailsContainerTarget.classList.remove('d-none');
      this.inputTypeValue = 'post_code';

      /* Populate postcodes addresses results */
      let addresses, error;
      this.loadingValue = true;
      try {
        ({ addresses, error } = await fetchPostcodeAddresses(this.postCodeTarget.value, this.idealPostcodesApiKeyValue));
      } finally {
        this.loadingValue = false;
      }

      if (error) {
        return;
      }

      /* Populate the select */
      await this.renderPostcodeResults(addresses, this.addressValue.udprn);

      /* Hide flow switcher */
      this.flowSwitcherTarget.classList.add('d-none');
    } else if (
      this.addressValue.address &&
      this.addressValue.latitude &&
      this.addressValue.longitude &&
      this.addressValue.place_id
    ) {
      /* Show an existing google address */
      this.inputTypeValue = 'google';
      this.queryContainerTarget.classList.remove('d-none');
      this.queryTarget.value = this.addressValue.address;
      this.queryLabelTarget.textContent = 'Address';
      this.queryTarget.placeholder = 'Enter address';
      this.addressTarget.value = this.addressValue.address;
      this.latitudeTarget.value = this.addressValue.latitude;
      this.longitudeTarget.value = this.addressValue.longitude;
      this.placeIdTarget.value = this.addressValue.place_id;
      this.addressDetailsTarget.value = this.addressValue.address_details;
      this.addressDetailsContainerTarget.classList.remove('d-none');
      this.flowSwitcherTarget.classList.add('d-none');
    } else {
      // No address data, show the default form
      this.inputTypeValue = this.defaultInputTypeValue;
      this.resetForm();
    }

    // If editing an existing address, show the map
    if (this.addressValue.latitude && this.addressValue.longitude) {
      await this.showMap(parseFloat(this.addressValue.latitude), parseFloat(this.addressValue.longitude));
    }

    /* When editing existing address */
    if(this.editValue){
      /* Disable editing */
      this.queryTarget.setAttribute('disabled', 'disabled');
      this.udprnTarget.setAttribute('disabled', 'disabled');
    }
  }

  loadingValueChanged(value) {
    document.getElementById('propertyForm').ariaBusy = value;
  }

  /**
   * Handles showing the query error message.
   * @param value
   */
  queryErrorValueChanged(value) {
    if (!value) {
      this.queryErrorTarget.classList.add('d-none');
      this.queryErrorTarget.innerText = '';
      this.queryErrorCountValue = 0;
    } else {
      this.queryErrorTarget.classList.remove('d-none');
      this.queryErrorTarget.innerText = value;
    }
  }

  /**
   * Resets form and its data to empty state. It leaves your query intact.
   */
  resetForm() {
    this.resetData();
    this.queryErrorValue = '';

    this.hideMap();
    // If postCodeSupported is false, always use google input type and hide flow switcher
    if (this.hasPostCodeSupportedValue && this.postCodeSupportedValue === false) {
      this.queryLabelTarget.textContent = 'Address';
      this.queryTarget.placeholder = 'Enter address';
      this.queryContainerTarget.classList.remove('d-none');
      this.udprnContainerTarget.classList.add('d-none');
      this.addressDetailsContainerTarget.classList.add('d-none');
      this.flowSwitcherTarget.classList.add('d-none');
      return;
    }
    if (this.inputTypeValue === 'post_code') {
      /* Input type specific labels */
      this.queryLabelTarget.textContent = 'Postcode';
      this.queryTarget.placeholder = 'Enter postcode';

      /* UI elements */
      this.queryContainerTarget.classList.remove('d-none');
      this.udprnContainerTarget.classList.add('d-none');
      this.addressDetailsContainerTarget.classList.add('d-none');
      this.flowSwitcherTarget.classList.remove('d-none');
    } else {
      /* Input type specific labels */
      this.queryLabelTarget.textContent = 'Address';
      this.queryTarget.placeholder = 'Enter address';

      /* UI elements */
      this.queryContainerTarget.classList.remove('d-none');
      this.udprnContainerTarget.classList.add('d-none');
      this.addressDetailsContainerTarget.classList.add('d-none');
      this.flowSwitcherTarget.classList.remove('d-none');
    }
  }

  /**
   * Resets all the important data in the form.
   */
  resetData() {
    this.addressTarget.value = '';
    this.latitudeTarget.value = '';
    this.longitudeTarget.value = '';
    this.placeIdTarget.value = '';

    this.addressDetailsTarget.value = '';
    this.addressResultsTarget.innerHTML = '';
    this.addressResultsTarget.classList.remove('show');
    this.addressDetailsContainerTarget.classList.add('d-none');
  }

  /**
   * Changes the input type between postcode and Google Places API.
   * @param event
   */
  changeInputType(event) {
    event.preventDefault();
    // Prevent switching if postCodeSupported is false
    if (this.hasPostCodeSupportedValue && this.postCodeSupportedValue === false) {
      return;
    }
    if (this.inputTypeValue === 'post_code') {
      this.inputTypeValue = 'google';
    } else {
      this.inputTypeValue = 'post_code';
    }
    this.resetForm();
  }

  /**
   * Handles input in the query field. Forwards the input to the correct handler based on the input type.
   * @param event
   */
  onQueryInput(event) {
    const query = event.target.value;
    if (this.inputTypeValue === 'post_code') {
      this.onPostCodeInput(query);
    } else {
      this.onGoogleInput(query);
    }
  }

  /**
   * When the query input is blurred, we need to fill back the query with the postcode or address. It's not a free field.
   * @param event
   */
  onQueryBlurred(event) {
    if (this.inputTypeValue === 'google') {
      if (this.addressTarget.value) {
        event.target.value = this.addressTarget.value;
      }
    }
  }

  /**
   * Handles postcode input.
   * @param query
   * @return {Promise<void>}
   */
  async onPostCodeInput(query) {
    const trimmedPostcode = query.replace(/\s+/g, '');
    if (trimmedPostcode.length < 4) {
      this.resetForm();
      return;
    }

    /* Query the api */
    let formattedPostcode, addresses, error;
    this.loadingValue = true;
    try {
      ({
        formattedPostcode,
        addresses,
        error
      } = await fetchPostcodeAddresses(trimmedPostcode, this.idealPostcodesApiKeyValue));
    } finally {
      this.loadingValue = false;
    }

    if (error) {
      this.resetForm();
      this.displayQueryError(error);
      return;
    }

    /* Reset the error */
    this.queryErrorValue = '';

    /* Save this for future usage */
    this.postcodeResultsValue = addresses;

    /* Render the dropdown */
    this.renderPostcodeResults(addresses);

    /* Set the postcode */
    this.queryTarget.value = formattedPostcode;
    this.postCodeTarget.value = formattedPostcode;

    /* Hide switcher */
    this.flowSwitcherTarget.classList.add('d-none');

    /* Show the address details container */
    this.addressDetailsContainerTarget.classList.remove('d-none');
  }

  /**
   * Handles when to display an error message for the query.
   * @param errorMessage
   */
  displayQueryError(errorMessage) {
    if (this.queryErrorCountValue > 1) {
      this.queryErrorValue = errorMessage;
    }
    this.queryErrorCountValue += 1;
  }

  /**
   * Renders postcode results in the dropdown and selects the correct one if provided.
   * If udprn is not provided, it will select the first result.
   * @param results
   * @param udprn
   */
  async renderPostcodeResults(results, udprn = null) {
    this.postcodeResultsValue = results;
    this.udprnTarget.innerHTML = '';

    results.forEach(address => {
      const option = document.createElement('option');
      option.value = address.udprn;
      option.textContent = [address.line_1, address.line_2, address.line_3, address.post_town].filter(Boolean).join(', ');
      this.udprnTarget.appendChild(option);
    });

    if (udprn) {
      const selectedAddress = results.find(address => String(address.udprn) === String(udprn));
      this.udprnTarget.value = selectedAddress.udprn;
      await this.selectPostcodeResult(selectedAddress);
    } else {
      this.udprnTarget.value = results[0].udprn;
      await this.selectPostcodeResult(results[0]);
    }

    this.udprnContainerTarget.classList.remove('d-none');
  }

  /**
   * Handles change in the UDPRN select element, because we need to update latitude etc. based on the selected result.
   * @param event
   * @return {Promise<void>}
   */
  async onUdprnChanged(event) {
    const udprn = event.target.value;
    const selectedAddress = this.postcodeResultsValue.find(address => String(address.udprn) === String(udprn));

    if (!selectedAddress) {
      this.resetForm();
      return;
    }

    await this.selectPostcodeResult(selectedAddress);
  }

  /**
   * Sets the important data based on the selected postcode result.
   * @param result
   * @return {Promise<void>}
   */
  async selectPostcodeResult(result) {
    const address = [result.line_1, result.line_2, result.line_3, result.post_town].filter(Boolean).join(', ');
    let placeId;
    this.loadingValue = true;
    try {
      let retryCount = 0;
      const maxRetries = 3;

      do {
        placeId = await fetchPlaceIdForPostCodeResult(address, this.boundsValue);
        retryCount++;
        if (placeId === null && retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } while (placeId === null && retryCount < maxRetries);
    } finally {
      this.loadingValue = false;
    }
    this.addressTarget.value = address;
    this.latitudeTarget.value = result.latitude;
    this.longitudeTarget.value = result.longitude;
    this.postCodeTarget.value = result.postcode;
    this.placeIdTarget.value = placeId;

    this.showMap(parseFloat(result.latitude), parseFloat(result.longitude));
  }

  /**
   * Handles input for Google Places API.
   * @param query
   * @return {Promise<void>}
   */
  async onGoogleInput(query) {
    if (query.length < 3) {
      this.resetForm();
      return;
    }

    this.loadingValue = true;
    try {
      /* Show predictions in dropdown */
      const showPredictions = (predictions) => {
        this.addressResultsTarget.innerHTML = '';
        currentIndex = -1;

        if (predictions.length > 0) {
          predictions.forEach((prediction, index) => {
            const item = document.createElement('a');
            item.classList.add('dropdown-item');
            item.innerHTML = prediction.formattedAddress;
            item.href = '#';
            item.dataset.index = index;

            item.addEventListener('click', (e) => {
              e.preventDefault();
              this.selectGooglePlace(prediction);
            });

            item.addEventListener('mouseover', () => {
              currentIndex = index;
              highlightPrediction(index);
            });

            this.addressResultsTarget.appendChild(item);
          });

          this.addressResultsTarget.classList.add('show');
        } else {
          this.addressResultsTarget.classList.remove('show');
        }
      };

      /* Highlight the currently selected prediction */
      const highlightPrediction = (index) => {
        const items = this.addressResultsTarget.querySelectorAll('.dropdown-item');
        items.forEach((item) => item.classList.remove('active'));

        if (index >= 0) {
          items[index].classList.add('active');
          items[index].scrollIntoView({block: 'nearest'});
        }
      };

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!this.queryTarget.contains(e.target)) {
          this.addressResultsTarget.classList.remove('show');
        }
      });

      /* Query the Google Places API */
      const {Place} = await google.maps.importLibrary('places');
      let currentIndex = -1;
      let predictions = [];

      const result = await Place.searchByText({
        textQuery: query,
        fields: ['formattedAddress', 'location'],
        maxResultCount: 5,
        locationRestriction: this.boundsValue,
      });

      predictions = result.places;
      showPredictions(predictions);

      // Handle keyboard navigation
      this.queryTarget.addEventListener('keydown', (e) => {
        if (!predictions.length) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          currentIndex = Math.min(currentIndex + 1, predictions.length - 1);
          highlightPrediction(currentIndex);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          currentIndex = Math.max(currentIndex - 1, -1);
          highlightPrediction(currentIndex);
        } else if (e.key === 'Enter' && currentIndex >= 0) {
          e.preventDefault();
          this.selectGooglePlace(predictions[currentIndex]);
        }
      });
    } finally {
      this.loadingValue = false;
    }
  }

  /**
   * Handles selection of a Google Place prediction.
   * @param prediction
   */
  selectGooglePlace(prediction) {
    /* Save data */
    this.addressTarget.value = prediction.formattedAddress;
    this.latitudeTarget.value = prediction.location.lat();
    this.longitudeTarget.value = prediction.location.lng();
    this.placeIdTarget.value = prediction.id;

    /* Show address details */
    this.addressDetailsContainerTarget.classList.remove('d-none');
    this.addressDetailsTarget.value = '';

    /* Replace query with a nice address */
    this.queryTarget.value = prediction.formattedAddress;

    /* Hide the results */
    this.addressResultsTarget.classList.remove('show');

    /* Remove flow switcher */
    this.flowSwitcherTarget.classList.add('d-none');

    this.showMap(prediction.location.lat(), prediction.location.lng());
  };

  /**
   * Show Google Map on provided location. User can drag the marker and it will update the lat/lng of the property.
   * @param lat
   * @param lng
   * @return {Promise<void>}
   */
  async showMap(lat, lng) {
    if (!window.google || !window.google.maps) return;
    this.mapContainerTarget.classList.remove('d-none');

    const { Map } = await google.maps.importLibrary("maps")
    const { Marker } = await google.maps.importLibrary("marker")

    if (!this._map) {
      this._map = new Map(document.getElementById('address-map'), {
        center: {lat, lng},
        zoom: 16,
        disableDefaultUI: true
      });
      this._marker = new Marker({
        position: {lat, lng},
        map: this._map,
        draggable: true
      });
      this._marker.addListener('dragend', (e) => {
        this.latitudeTarget.value = e.latLng.lat();
        this.longitudeTarget.value = e.latLng.lng();
      });
    } else {
      this._map.setCenter({lat, lng});
      this._marker.setPosition({lat, lng});
    }
  }

  hideMap() {
    this.mapContainerTarget.classList.add('d-none');
  }
}
